/* ═══════════════════════════════════════════
   assets/cart-drawer.js
   The cart drawer (mobile Phase 7, Batches 10, 10b and 11; CLAUDE.md rule
   68). Markup: sections/cart-drawer.liquid, rendered in layout/theme.liquid
   and re-rendered after every add or change through the Section Rendering
   API. This file swaps [data-cart-drawer-inner] and never builds cart
   markup: even the rec pick is the same section, rendered by
   /recommendations/products?section_id=cart-drawer after the drawer opens.
   The pick's + ADD adds in place through the same queue as the steppers.
   v4 motion (Batch 13): after a re-render a new line slides in and flashes,
   the gift bar's newly lit segments fill one by one and the header count
   bumps; CSS classes only, never under prefers-reduced-motion.
   Batch 14, the gift picker: the ladder's <select> adds the picked gift in
   place with the line property _gift (its tier), and a pick that does not
   land at $0.00 is removed at once; the upgrade swaps a tier 1 gift for a
   tier 2 one only if the new one is free. The gift guard runs after every
   render and before CHECKOUT: a _gift line that is no longer $0.00 is
   removed and one line says why. Nothing is ever charged for a gift.
   Batch 14b, sold out and over stock: a sold-out line has one trash that
   removes it from any quantity (Shopify refuses every other quantity with a
   422), CHECKOUT stops while one is in the cart, an over-stock minus steps
   straight to the stock with a note, the plus stops at stock, and a 422 on a
   decrement re-reads the drawer once instead of showing Shopify's text.
   The gift code (2026-10-02): with the X ad's XFIRSTGUMMY on the cart,
   syncCode() sets the cart attribute _gift_code, which the section reads, so
   tier 1 offers the gummies (rule 68's gift code amendment).

   Public API, window.PinePark.cartDrawer:
     added(item, displayName)  after a successful /cart/add.js (theme.js,
                               quick-add.js). The first add in a browser
                               session opens the drawer; later ones show the
                               bar (decision D, the hybrid).
     failed(description)       after a failed add: opens the drawer with the
                               one plain error line.
     open(trigger) / close()
     sectionParams()           { sections, sections_url } to merge into an
                               add or change body, so the response carries the
                               re-rendered drawer.
   Vanilla ES5.
   ═══════════════════════════════════════════ */
(function () {
  'use strict';

  var SECTION = 'cart-drawer';
  var SEEN_KEY = 'pp_cart_drawer_seen';
  var TOAST_MS = 2000;

  var drawer, panel, toast, toastName;
  var isOpen = false;
  var lastFocus = null;
  var lockedY = 0;
  var historyPushed = false;
  var ignorePop = 0;
  var pendingNav = null;
  var toastTimer = null;
  var live = null;
  var pendingHash = null;
  var recCache = {};
  var parser = window.DOMParser ? new window.DOMParser() : null;

  /* Change queue: taps set a target per line; one job per line runs at a
     time and reads the latest target when it starts, so fast taps collapse
     into as few requests as possible and never race. Lines are tracked by
     data-line-sig (variant and properties), because Shopify can hand a line
     a new key when its quantity changes; keys[] follows each re-render. */
  var queue = Promise.resolve();
  var targets = {};
  var from = {};
  var stockNotes = {};
  var recheck = [];
  var learned = {};
  var queued = {};
  var keys = {};
  var inflight = 0;
  var lastHtml = null;
  var lastError = null;

  /* The gift guard's state, and the one line it leaves in the drawer (kept
     across re-renders until the drawer closes or the next tap). */
  var notice = '';
  var guardTried = {};
  var GIFT_REFUSED = 'Your deal already saves more than the free gift, so it wasn\'t added.';
  var GIFT_NOT_FREE = 'That gift isn\'t free with this cart, so it wasn\'t added.';

  function sectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  function sectionParams() {
    return { sections: SECTION, sections_url: sectionsUrl() };
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function readJSON(res) {
    return res.json().catch(function () { return {}; }).then(function (data) {
      if (!res.ok) return Promise.reject(data || {});
      return data;
    });
  }

  /* Shopify's own description when it gave one; never the browser's
     network message. On a landing every error is the generic line, because
     Shopify's text can name a product by its raw title (rule 67's words). */
  function errorText(err) {
    if (isLanding()) return 'Could not add that. It may be at its stock limit.';
    if (err && err.description) return String(err.description);
    return 'Could not update your cart. Please try again.';
  }

  function isLanding() {
    return !!drawer && drawer.getAttribute('data-landing') === 'true';
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(readJSON);
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { 'Accept': 'application/json' } })
      .then(readJSON);
  }

  /* Set these lines (by key) to 0; resolves to the re-rendered drawer. */
  function removeLines(lineKeys) {
    var body = sectionParams();
    body.updates = {};
    for (var i = 0; i < lineKeys.length; i++) body.updates[lineKeys[i]] = 0;
    return postJSON('/cart/update.js', body).then(function (cart) {
      return cart.sections ? cart.sections[SECTION] : null;
    });
  }

  function announce(text) {
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 50);
  }

  function fetchSection() {
    var url = sectionsUrl();
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'sections=' + SECTION;
    return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
      .then(readJSON)
      .then(function (data) { return data[SECTION] || null; });
  }

  function focusables() {
    var list = panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].offsetParent !== null) out.push(list[i]);
    }
    return out;
  }

  function findByFocusId(id) {
    var list = panel.querySelectorAll('[data-focus-id]');
    for (var i = 0; i < list.length; i++) {
      if (list[i].getAttribute('data-focus-id') === id) return list[i];
    }
    return null;
  }

  function setCount(n) {
    var els = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < els.length; i++) els[i].textContent = n;
  }

  function showError(text) {
    var els = panel.querySelectorAll('[data-cart-drawer-error]');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = text || '';
      els[i].hidden = !text;
    }
  }

  /* What the drawer showed before a re-render: its line signatures, the
     gift bar's lit segments and the item count. */
  function snapshot(inner) {
    var sigs = {};
    var lines = inner.querySelectorAll('[data-line-sig]');
    for (var i = 0; i < lines.length; i++) sigs[lines[i].getAttribute('data-line-sig')] = true;
    var segs = inner.querySelector('[data-cart-drawer-segs]');
    return {
      sigs: sigs,
      fill: segs ? parseInt(segs.getAttribute('data-fill'), 10) || 0 : 0,
      count: parseInt(inner.getAttribute('data-item-count'), 10) || 0
    };
  }

  /* v4 motion, classes only (sections.css): a line that was not there
     slides in and flashes, the segments newly lit fill one by one, and the
     count bumps when it grows. */
  function animate(inner, before) {
    if (reducedMotion()) return;
    var lines = inner.querySelectorAll('[data-line-sig]');
    for (var i = 0; i < lines.length; i++) {
      if (!before.sigs[lines[i].getAttribute('data-line-sig')]) lines[i].classList.add('is-new');
    }
    var segBox = inner.querySelector('[data-cart-drawer-segs]');
    var fill = segBox ? parseInt(segBox.getAttribute('data-fill'), 10) || 0 : 0;
    if (segBox && fill > before.fill) {
      var segs = segBox.querySelectorAll('.cart-drawer-seg');
      for (var n = before.fill; n < fill && n < segs.length; n++) {
        segs[n].style.setProperty('--i', n - before.fill);
        segs[n].classList.add('is-fill');
      }
    }
    var count = parseInt(inner.getAttribute('data-item-count'), 10) || 0;
    var countEl = inner.querySelector('.cart-drawer-title span');
    if (countEl && count > before.count) countEl.classList.add('is-bump');
  }

  /* Swap in a freshly rendered drawer. Keeps focus on the same control
     (data-focus-id) when it still exists, else on the panel. Motion plays
     when the drawer is open or about to open (withMotion), never behind
     the hybrid's bar, where it would be cut short by the next refresh. */
  function render(html, withMotion) {
    if (!html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var fresh = tmp.querySelector('[data-cart-drawer-inner]');
    var inner = panel.querySelector('[data-cart-drawer-inner]');
    if (!fresh || !inner) return;

    var active = document.activeElement;
    var focusId = active && panel.contains(active) ? active.getAttribute('data-focus-id') : null;
    var hadFocus = active && panel.contains(active);
    var before = snapshot(inner);

    inner.innerHTML = fresh.innerHTML;
    /* The count, the total and the tiers ride on the inner itself. */
    for (var a = 0; a < fresh.attributes.length; a++) {
      if (fresh.attributes[a].name.indexOf('data-') === 0) inner.setAttribute(fresh.attributes[a].name, fresh.attributes[a].value);
    }
    setCount(fresh.getAttribute('data-item-count') || '0');
    if (withMotion || isOpen) animate(inner, before);

    if (hadFocus) {
      var again = focusId ? findByFocusId(focusId) : null;
      (again || panel).focus();
    }
    if (notice) showError(notice);
    syncStock();
    if (isOpen) loadRec();
    guardGifts();
  }

  /* CLAUDE.md rule 4: iOS-safe scroll lock. */
  function lockScroll() {
    lockedY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + lockedY + 'px';
    document.body.style.width = '100%';
  }

  /* Jump back, do not glide: html carries scroll-behavior smooth
     (base.css), so it is switched off for the restore, as the nav menu does. */
  function unlockScroll() {
    var root = document.documentElement;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockedY);
    root.style.scrollBehavior = '';
  }

  function open(trigger) {
    if (!drawer || isOpen) return;
    hideToast();
    lastFocus = trigger || document.activeElement;
    isOpen = true;
    /* The history entry first, while the page still has its scroll
       position, so the entry it covers keeps that position. */
    try {
      window.history.pushState({ cartDrawer: true }, '');
      historyPushed = true;
    } catch (e) {
      historyPushed = false;
    }
    lockScroll();
    drawer.hidden = false;
    void drawer.offsetHeight;
    drawer.classList.add('is-open');
    try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    loadRec();
  }

  function close(fromPop) {
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    setTimeout(function () {
      if (!isOpen) drawer.hidden = true;
    }, reducedMotion() ? 0 : 250);
    unlockScroll();
    notice = '';
    showError('');

    /* Closed by X, the strip or Escape: take back the entry open pushed, so
       the phone's back button goes to the previous page. */
    if (historyPushed) {
      historyPushed = false;
      if (!fromPop) {
        ignorePop++;
        window.history.back();
      }
    }

    var target = lastFocus;
    if (!target || target.offsetParent === null || !document.body.contains(target)) {
      target = document.querySelector('.nav-cart');
    }
    if (target && typeof target.focus === 'function') target.focus();
    lastFocus = null;
  }

  /* Leaving the page from inside the open drawer (a link, CHECKOUT): take
     back the drawer's history entry first, then go, so back from the next
     page lands on this one and not on a drawer state that no longer exists. */
  function leave(url) {
    if (!historyPushed) {
      window.location.href = url;
      return;
    }
    historyPushed = false;
    pendingNav = url;
    window.history.back();
    setTimeout(function () {
      if (pendingNav) {
        var go = pendingNav;
        pendingNav = null;
        window.location.href = go;
      }
    }, 400);
  }

  function onPopState() {
    if (pendingNav) {
      var go = pendingNav;
      pendingNav = null;
      window.location.href = go;
      return;
    }
    if (ignorePop > 0) {
      ignorePop--;
      goToHash();
      return;
    }
    if (isOpen) close(true);
  }

  /* A same-page link inside the drawer (the landing's nudge to
     #lp-singles): close through the normal path, then scroll there once the
     drawer's history entry is gone and the page's scroll is restored. */
  function goToHash() {
    if (!pendingHash) return;
    var target = document.getElementById(pendingHash.slice(1));
    pendingHash = null;
    if (!target) return;
    /* A tick later, so the traversal's own scroll restore cannot undo it. */
    setTimeout(function () { target.scrollIntoView({ block: 'start' }); }, 60);
  }

  /* The hybrid's bar: shown and hidden with a class. The announcement goes
     through the separate live region, which stays in the tree. */
  function showToast(name) {
    if (!toast) return;
    clearTimeout(toastTimer);
    if (toastName) toastName.textContent = name || '';
    toast.classList.add('is-shown');
    if (live) {
      live.textContent = '';
      setTimeout(function () { live.textContent = 'Added ' + (name || 'to cart'); }, 50);
    }
    toastTimer = setTimeout(function () { hideToast(); }, TOAST_MS);
  }

  function hideToast() {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.classList.remove('is-shown');
  }

  function seenThisSession() {
    try {
      if (window.sessionStorage.getItem(SEEN_KEY) === '1') return true;
      window.sessionStorage.setItem(SEEN_KEY, '1');
      return false;
    } catch (e) {
      /* No storage: always open. */
      return false;
    }
  }

  function added(item, displayName) {
    if (!drawer) return;
    var html = item && item.sections && !isLanding() ? item.sections[SECTION] : null;
    var ready = html ? Promise.resolve(html) : fetchSection().catch(function () { return null; });
    ready.then(function (fresh) {
      if (isOpen) {
        render(fresh);
        return;
      }
      if (seenThisSession()) {
        render(fresh);
        showToast(displayName || (item && (item.product_title || item.title)) || '');
      } else {
        render(fresh, true);
        open();
      }
    });
  }

  function failed(description) {
    if (!drawer) return;
    fetchSection().catch(function () { return null; }).then(function (fresh) {
      render(fresh);
      if (!isOpen) open();
      showError(errorText({ description: description }));
    });
  }

  /* Current key of every line in a rendered drawer, by signature. */
  function readKeys(html) {
    if (!html) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var lines = tmp.querySelectorAll('[data-line-sig]');
    var next = {};
    for (var i = 0; i < lines.length; i++) {
      next[lines[i].getAttribute('data-line-sig')] = lines[i].getAttribute('data-line-key');
    }
    for (var sig in keys) {
      if (keys.hasOwnProperty(sig)) keys[sig] = next[sig] || null;
    }
  }

  /* Every cart write from inside the drawer (steppers and the pick's
     + ADD) runs through this one queue: one request at a time, and one
     re-render once it drains. A job never rejects; it leaves lastHtml or
     lastError behind. */
  function enqueue(job) {
    inflight++;
    queue = queue.then(job).then(function () {
      inflight--;
      if (inflight > 0) return;
      targets = {};
      from = {};
      keys = {};
      var err = lastError;
      var checks = recheck;
      recheck = [];
      var html = lastHtml;
      lastError = null;
      lastHtml = null;
      /* On a landing, never a write's own render: Shopify drops ?view=landing
         from sections_url, so that render is the off-landing drawer (meta
         lines, full labels, a rec slot). A GET keeps the view (Batch 14b). */
      var done = (err || !html || isLanding()) ? fetchSection().catch(function () { return null; }) : Promise.resolve(html);
      return done.then(function (fresh) {
        render(fresh);
        if (err) showError(err);
        if (checks.length) afterRecheck(checks);
      });
    });
  }

  /* A line's limit: its stock when Shopify enforces it (tracked,
     "continue selling" off: data-track "shopify:deny"), or, when lower, the
     quantity Shopify clamped it to on a refused change (a per-order cap
     Liquid cannot read: Versace stocks 382 but the cart stops at 20); else
     null. */
  function lineStock(line) {
    var sig = line.getAttribute('data-line-sig');
    var cap = learned.hasOwnProperty(sig) ? learned[sig] : null;
    if (line.getAttribute('data-track') !== 'shopify:deny') return cap;
    var n = parseInt(line.getAttribute('data-stock'), 10);
    if (isNaN(n)) return cap;
    n = Math.max(0, n);
    return cap === null ? n : Math.min(n, cap);
  }

  function lineBySig(sig) {
    var lines = panel.querySelectorAll('[data-line-sig]');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-line-sig') === sig) return lines[i];
    }
    return null;
  }

  /* The plus stops at stock (a free line's plus is disabled in Liquid). */
  function syncPlus(line, n) {
    var stock = lineStock(line);
    var plus = line.querySelector('[data-cart-drawer-change="plus"]');
    if (!plus || stock === null || line.getAttribute('data-free') === '1') return;
    plus.disabled = n >= stock;
  }

  function syncStock() {
    var lines = panel.querySelectorAll('[data-line-sig]');
    for (var i = 0; i < lines.length; i++) {
      var countEl = lines[i].querySelector('[data-cart-drawer-count]');
      syncPlus(lines[i], parseInt(countEl ? countEl.textContent : '0', 10) || 0);
    }
  }

  /* After a change Shopify refused (422): the drawer has been re-read. A
     line now sold out shows its SOLD OUT state (the trash). One that came
     back lower than asked was clamped to its limit: that count is learned
     (the plus stops there) and the note says so. Anything else gets the
     plain line, never Shopify's own text. */
  function afterRecheck(checks) {
    for (var i = 0; i < checks.length; i++) {
      var line = lineBySig(checks[i].sig);
      if (!line || line.getAttribute('data-available') === 'false') continue;
      var countEl = line.querySelector('[data-cart-drawer-count]');
      var n = parseInt(countEl ? countEl.textContent : '0', 10) || 0;
      if (n > 0 && n < checks[i].target) {
        learned[checks[i].sig] = n;
        syncPlus(line, n);
        notice = 'Only ' + n + ' left' + (checks[i].decrement ? ', quantity updated.' : '.');
        showError(notice);
      } else {
        showError(isLanding() ? errorText(null) : 'Could not update your cart. Please try again.');
      }
    }
  }

  /* The minus button is a trash icon at 1 (CSS reads data-qty); keep it and
     its label in step with the count the taps have reached. */
  function setLineQty(line, n) {
    var countEl = line.querySelector('[data-cart-drawer-count]');
    if (countEl) countEl.textContent = n;
    line.setAttribute('data-qty', n === 1 ? '1' : 'n');
    var minus = line.querySelector('[data-cart-drawer-change="minus"]');
    if (minus) {
      var label = minus.getAttribute(n === 1 ? 'data-label-one' : 'data-label-less');
      if (label) minus.setAttribute('aria-label', label);
    }
  }

  /* Minus (Remove at 1) and plus */
  function change(btn) {
    var line = btn.closest('[data-line-key]');
    if (!line) return;
    var sig = line.getAttribute('data-line-sig');
    if (!keys.hasOwnProperty(sig)) keys[sig] = line.getAttribute('data-line-key');
    var action = btn.getAttribute('data-cart-drawer-change');
    var countEl = line.querySelector('[data-cart-drawer-count]');
    var current = targets.hasOwnProperty(sig) ? targets[sig] : parseInt(countEl ? countEl.textContent : '0', 10) || 0;
    var next = action === 'plus' ? current + 1 : (action === 'minus' ? Math.max(0, current - 1) : 0);
    var stock = lineStock(line);
    if (!from.hasOwnProperty(sig)) from[sig] = parseInt(countEl ? countEl.textContent : '0', 10) || 0;

    /* Sold out: any quantity but 0 is refused, so every tap removes it.
       Over stock: the minus steps straight to the stock. */
    if (line.getAttribute('data-available') === 'false') {
      next = 0;
    } else if (action === 'minus' && stock !== null && stock > 0 && current > stock) {
      next = stock;
      stockNotes[sig] = 'Only ' + stock + ' left, quantity updated.';
    } else if (action === 'plus' && stock !== null && current >= stock) {
      return;
    }
    if (next !== stock) delete stockNotes[sig];

    targets[sig] = next;
    setLineQty(line, next);
    syncPlus(line, next);
    line.classList.add('is-busy');
    line.setAttribute('aria-busy', 'true');
    notice = '';
    showError('');

    if (queued[sig]) return;
    queued[sig] = true;

    enqueue(function () {
      queued[sig] = false;
      /* The line left the cart (removed by an earlier job): nothing to do. */
      if (!keys[sig]) return;
      var body = sectionParams();
      var decrement = targets[sig] < from[sig];
      body.id = keys[sig];
      body.quantity = targets[sig];
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(readJSON)
        .then(function (cart) {
          lastHtml = cart.sections ? cart.sections[SECTION] : null;
          lastError = null;
          readKeys(lastHtml);
          if (stockNotes[sig]) {
            notice = stockNotes[sig];
            delete stockNotes[sig];
          }
        })
        .catch(function (err) {
          /* A refused change (sold out, or a lower limit than the drawer
             knew): re-read once, then afterRecheck says what happened. */
          if (err && err.status === 422) {
            lastError = null;
            lastHtml = null;
            recheck.push({ sig: sig, target: body.quantity, decrement: decrement });
            return;
          }
          lastError = errorText(err);
        });
    });
  }

  /* The pick's + ADD: added in place, the drawer stays open and re-renders
     (the pick then moves on or goes). Not theme.js's card path, which would
     rewrite the button's label and hand the result to the hybrid. */
  function addInPlace(btn) {
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId || btn.getAttribute('aria-disabled') === 'true') return;
    var pick = btn.closest('.cart-drawer-pick');
    btn.setAttribute('aria-disabled', 'true');
    if (pick) {
      pick.classList.add('is-busy');
      pick.setAttribute('aria-busy', 'true');
    }
    notice = '';
    showError('');

    enqueue(function () {
      var body = sectionParams();
      body.id = variantId;
      body.quantity = 1;
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(readJSON)
        .then(function (item) {
          lastHtml = item.sections ? item.sections[SECTION] : null;
          lastError = null;
          readKeys(lastHtml);
          if (live) {
            live.textContent = '';
            setTimeout(function () { live.textContent = 'Added ' + (btn.getAttribute('data-name') || 'to cart'); }, 50);
          }
        })
        .catch(function (err) {
          lastError = errorText(err);
        });
    });
  }

  /* The gift picker's change, one queued job so the drawer renders once,
     when it is all done: add the pick with _gift (its tier); take out any
     gift of the other tier (one free gift per order: the upgrade), then read
     the cart. Shopify frees one item per promo and may choose which, so the
     pick is judged only after the old gift is gone. At $0.00 it stays; if
     not (a deal saves more, or the promo does not cover it) it is removed at
     once, the old gift goes back, and one line says why. Shopify frees the
     earliest-added item the promo covers, so when it freed one of the
     shopper's own lines (a gummy they were buying) instead of the pick, that
     line is added again once, behind the pick, and the pick judged again. */
  function pickGift(sel) {
    var variantId = parseInt(sel.value, 10);
    if (!variantId) return;
    var tier = sel.getAttribute('data-tier') || '1';
    var opt = sel.options[sel.selectedIndex];
    var name = opt ? (opt.getAttribute('data-name') || opt.text) : '';
    var box = sel.closest('[data-cart-drawer-picker]');
    if (box) {
      box.classList.add('is-busy');
      box.setAttribute('aria-busy', 'true');
    }
    sel.disabled = true;
    notice = '';
    showError('');

    function giftOf(item) {
      return item.properties && item.properties._gift ? String(item.properties._gift) : '';
    }

    function lineItems(list) {
      var out = [];
      for (var i = 0; i < list.length; i++) out.push({ id: list[i].variant_id, quantity: list[i].quantity, properties: list[i].properties });
      return out;
    }

    function lineKeys(list) {
      var out = [];
      for (var i = 0; i < list.length; i++) out.push(list[i].key);
      return out;
    }

    var old = [];

    function judge(cart, mayReseat) {
      var mine = null;
      var deal = false;
      var seat = [];
      var items = cart.items || [];
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!mine && item.variant_id === variantId && giftOf(item) === tier) mine = item;
        else if (!giftOf(item) && item.final_line_price === 0 && item.original_line_price > 0) seat.push(item);
        else if (item.line_level_total_discount > 0 && item.final_line_price > 0) deal = true;
      }
      if (!mine) return null;
      if (mine.final_line_price === 0) {
        announce('Added ' + (name || 'your free gift') + ' as your free gift');
        return null;
      }
      if (mayReseat && seat.length) {
        return removeLines(lineKeys(seat))
          .then(function () { return postJSON('/cart/add.js', { items: lineItems(seat) }); })
          .then(getCart)
          .then(function (again) { return judge(again, false); });
      }
      notice = deal ? GIFT_REFUSED : GIFT_NOT_FREE;
      return removeLines([mine.key]).then(function (html) {
        if (!old.length) return html;
        return postJSON('/cart/add.js', { items: old }).then(function () { return null; });
      });
    }

    enqueue(function () {
      old = [];
      lastHtml = null;
      return postJSON('/cart/add.js', { id: variantId, quantity: 1, properties: { _gift: tier } })
        .then(getCart)
        .then(function (cart) {
          var items = cart.items || [];
          for (var i = 0; i < items.length; i++) {
            var gift = giftOf(items[i]);
            if (gift && gift !== tier) old.push(items[i]);
          }
          var out = lineKeys(old);
          old = lineItems(old);
          return out.length ? removeLines(out) : null;
        })
        .then(getCart)
        .then(function (cart) { return judge(cart, true); })
        .then(function (html) {
          lastHtml = html || null;
          lastError = null;
          if (html) readKeys(html);
        })
        .catch(function (err) {
          lastError = errorText(err);
        });
    });
  }

  /* Why a charged gift left: another line took the one free gift, the cart
     fell under the gift's tier, or a deal saves more. */
  function guardReason(inner, charged, tier, freeName) {
    if (freeName) return 'One free gift per order: ' + freeName + ' is your free gift now.';
    var total = parseInt(inner.getAttribute('data-total'), 10) || 0;
    var need = parseInt(inner.getAttribute('data-tier' + tier), 10) || 0;
    var label = inner.getAttribute('data-tier' + tier + '-label') || '';
    if (need > 0 && total - charged < need) return 'Free gift removed: your cart is under ' + label + '.';
    return 'Free gift removed: your deal already saves more.';
  }

  /* The gift guard, after every render (and at load): any _gift line that is
     no longer $0.00 is removed, and one line says why. Never while the queue
     is busy (a pick or an upgrade is mid-way), and a key it could not remove
     is not tried again, so a failure cannot loop. */
  function guardGifts() {
    if (!panel || inflight > 0) return;
    var inner = panel.querySelector('[data-cart-drawer-inner]');
    if (!inner) return;
    var lines = inner.querySelectorAll('[data-line-key]');
    var out = [];
    var charged = 0;
    var tier = '1';
    var freeName = '';
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var gift = line.getAttribute('data-gift');
      if (line.getAttribute('data-free') === '1') {
        if (!freeName) freeName = line.getAttribute('data-name') || '';
        continue;
      }
      if (!gift) continue;
      var key = line.getAttribute('data-line-key');
      if (guardTried[key]) continue;
      guardTried[key] = true;
      out.push(key);
      charged += parseInt(line.getAttribute('data-price'), 10) || 0;
      if (gift === '2') tier = '2';
    }
    if (!out.length) return;
    var why = guardReason(inner, charged, tier, freeName);

    enqueue(function () {
      lastHtml = null;
      return removeLines(out)
        .then(function (html) {
          lastHtml = html || null;
          lastError = null;
          notice = why;
          if (html) readKeys(html);
        })
        .catch(function (err) {
          lastError = errorText(err);
        });
    });
  }

  /* The gift code (the X ad link /discount/XFIRSTGUMMY, 2026-10-02). Liquid
     cannot see a code that has not applied yet (cart.discount_codes is null
     there, measured), so the section reads the cart attribute _gift_code
     instead, and this keeps it true: when the link's discount_code cookie
     names the drawer's code (data-code) and /cart.js carries it, the
     attribute is set; when the drawer is in code mode but the cookie no
     longer names it and the cart no longer carries it, it is cleared. Then
     the drawer re-renders once. Nothing runs for anyone else: no cookie and
     no code mode means no request. */
  function cookieCodes() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m) return ',';
    var held = m[1];
    try { held = decodeURIComponent(held); } catch (e) {}
    return ',' + held.toUpperCase().replace(/\s+/g, '') + ',';
  }

  function syncCode() {
    var inner = panel ? panel.querySelector('[data-cart-drawer-inner]') : null;
    if (!inner) return;
    var code = (inner.getAttribute('data-code') || '').toUpperCase();
    if (!code) return;
    var on = inner.getAttribute('data-code-on') === '1';
    var held = cookieCodes().indexOf(',' + code + ',') !== -1;
    if (on === held) return;
    getCart().then(function (cart) {
      var codes = cart.discount_codes || [];
      var carried = false;
      for (var i = 0; i < codes.length; i++) {
        if (String(codes[i].code || '').toUpperCase() === code) carried = true;
      }
      if (carried === on) return;
      enqueue(function () {
        lastHtml = null;
        return postJSON('/cart/update.js', { attributes: { _gift_code: carried ? code : '' } })
          .catch(function () {});
      });
    }).catch(function () {});
  }

  /* CHECKOUT runs the same check against the live cart before it goes: a
     _gift line not at $0.00 is removed first. If the cart cannot be read,
     checkout goes ahead (it shows the real prices); if a charged gift cannot
     be removed, it stays here and says so. */
  function checkout(form) {
    /* A sold-out line blocks checkout: say so above the button and put
       focus on that line's trash (Batch 14b). */
    var sold = panel.querySelector('.cart-drawer-line[data-available="false"]');
    if (sold) {
      var block = panel.querySelector('[data-cart-drawer-block]');
      if (block) block.hidden = false;
      var trash = sold.querySelector('.cart-drawer-remove');
      if (trash) trash.focus();
      return;
    }
    var btn = form.querySelector('button');
    if (btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
    }
    var go = form.getAttribute('action') || '/checkout';
    queue = queue.then(function () {
      return getCart().then(function (cart) {
        var bad = [];
        var items = cart.items || [];
        for (var i = 0; i < items.length; i++) {
          if (items[i].properties && items[i].properties._gift && items[i].final_line_price > 0) bad.push(items[i].key);
        }
        if (!bad.length) return true;
        return removeLines(bad).then(function () { return true; }, function () { return false; });
      }, function () { return true; });
    }).then(function (ok) {
      if (ok) {
        leave(go);
        return;
      }
      if (btn) btn.removeAttribute('aria-busy');
      showError(errorText(null));
    });
  }

  /* The rec pick, off a landing when no deal pick is due. Loaded after the
     drawer opens: the recommendations endpoint renders this same section with
     recommendations.performed true, and its slot comes back with the card
     (the first related product that is available, single-variant and not in
     the cart) or empty. Nothing shows if it fails or returns none. Cached by
     the slot's key (product, total and cart ids), since the card depends on
     the cart. DOMParser, so the parsed copy's images never load. */
  function loadRec() {
    var slot = panel.querySelector('[data-cart-drawer-rec]');
    if (!slot || slot.getAttribute('data-loaded') || !parser) return;
    slot.setAttribute('data-loaded', '1');
    var pid = slot.getAttribute('data-product-id');
    if (!pid) return;
    var key = slot.getAttribute('data-rec-key') || pid;

    var got = recCache.hasOwnProperty(key) ? Promise.resolve(recCache[key]) :
      fetch('/recommendations/products?product_id=' + encodeURIComponent(pid) +
            '&intent=related&limit=10&section_id=' + SECTION, { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) return Promise.reject(res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = parser.parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-drawer-rec]');
          var card = fresh && fresh.querySelector('.cart-drawer-card') ? fresh.innerHTML : '';
          recCache[key] = card;
          return card;
        });

    got.then(function (card) {
      if (!card || !document.body.contains(slot)) return;
      slot.innerHTML = card;
      slot.hidden = false;
    }).catch(function () {});
  }

  function trapTab(e) {
    var items = focusables();
    if (!items.length) {
      e.preventDefault();
      panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function init() {
    drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    panel = drawer.querySelector('[data-cart-drawer-panel]');
    toast = document.querySelector('[data-cart-drawer-toast]');
    toastName = toast ? toast.querySelector('[data-cart-drawer-toast-name]') : null;
    live = document.querySelector('[data-cart-drawer-live]');

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-drawer-close]')) {
        e.preventDefault();
        close(false);
        return;
      }
      var step = e.target.closest('[data-cart-drawer-change]');
      if (step) {
        e.preventDefault();
        if (!step.disabled) change(step);
        return;
      }
      var add = e.target.closest('[data-cart-drawer-add]');
      if (add) {
        e.preventDefault();
        addInPlace(add);
        return;
      }
      var link = e.target.closest('a[href]');
      if (link && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey &&
          (!link.target || link.target === '_self')) {
        e.preventDefault();
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) === '#') {
          pendingHash = href;
          var pushed = historyPushed;
          close(false);
          if (!pushed) goToHash();
          return;
        }
        leave(link.href);
      }
    });

    drawer.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.classList.contains('cart-drawer-form')) return;
      e.preventDefault();
      checkout(form);
    });

    drawer.addEventListener('change', function (e) {
      var sel = e.target.closest ? e.target.closest('[data-cart-drawer-gift]') : null;
      if (sel) pickGift(sel);
    });

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-cart-drawer-open]');
      if (!opener || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      /* The bar's View cart hides with the bar: focus returns to CART. */
      var trigger = opener.closest('[data-cart-drawer-toast]') ? document.querySelector('.nav-cart') : opener;
      open(trigger);
      /* The drawer rendered with the page; refresh it quietly in case the
         cart changed in another tab or the page came from the cache. */
      fetchSection().then(render).catch(function () {});
    });

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.keyCode === 27) {
        close(false);
      } else if (e.key === 'Tab' || e.keyCode === 9) {
        trapTab(e);
      }
    });

    window.addEventListener('popstate', onPopState);

    /* Back-forward cache: the page can come back with a stale cart. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) fetchSection().then(render).catch(function () {});
    });

    window.PinePark = window.PinePark || {};
    window.PinePark.cartDrawer = {
      added: added,
      failed: failed,
      open: open,
      close: function () { close(false); },
      sectionParams: sectionParams
    };

    /* A gift left charged on another page (the /cart page, another tab) is
       caught at load, and the drawer says why when it opens. */
    syncStock();
    guardGifts();
    syncCode();
  }

  init();
})();
